import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { from, postgrestSource, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import { Button, Callout, DataTable, KpiRow, KpiTile, PageHeader, Section, StatusPill, addDaysIso, formatDate, formatInt, todayIso } from "@vixio/ui";
import { fmtDelta, fmtScore, type Alert, type CycleResult, type Team } from "./api";
import { TeamDialog } from "./TeamDialog";

/** Ciclos visíveis da organização (a view já aplica o mínimo de respostas). */
export function useOrgCycles(orgId: string) {
  return useQuery({
    queryKey: ["pulso", "v_cycle_results", orgId],
    queryFn: () => unwrap<CycleResult[]>(from("pulso", "v_cycle_results").select("*").eq("org_id", orgId).order("number", { ascending: false }).limit(2000)),
  });
}

export function useRecentAlerts(orgId: string, days = 90) {
  const since = addDaysIso(todayIso(), -days);
  return useQuery({
    queryKey: ["pulso", "alerts", orgId, since],
    queryFn: () => unwrap<Alert[]>(from("pulso", "alerts").select("*").eq("org_id", orgId).gte("created_at", `${since}T00:00:00-03:00`).order("created_at", { ascending: false })),
  });
}

function usePulsoInstalled(orgId: string) {
  return useQuery({
    queryKey: ["pulso", "form", orgId],
    queryFn: async () =>
      (await unwrap<Array<{ id: string }>>(from("forms", "forms").select("id").eq("org_id", orgId).eq("slug", "pulso").eq("status", "published").limit(1))).length > 0,
  });
}

export function TeamsPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const canWrite = useHasRole("manager");
  const isAdmin = useHasRole("admin");
  const [creating, setCreating] = useState(false);
  const cycles = useOrgCycles(org.id);
  const alerts = useRecentAlerts(org.id);
  const installed = usePulsoInstalled(org.id);
  const install = useAction(() => rpc("pulso", "install", { p_org: org.id }), { invalidate: [["pulso"]], success: "Pulso instalado" });

  const source = useMemo(
    () => postgrestSource<Team>({ schema: "pulso", table: "teams", orgId: org.id, searchColumns: ["name", "client_name"], filters: { active: (qb, v) => qb.eq("active", v === "sim") }, tiebreak: "id" }),
    [org.id],
  );
  const byTeam = useMemo(() => {
    const m = new Map<string, { open: CycleResult | null; lastReleased: CycleResult | null; count: number }>();
    for (const c of cycles.data ?? []) {
      const e = m.get(c.team_id) ?? { open: null, lastReleased: null, count: 0 };
      e.count++;
      if (c.status === "open") e.open = c;
      if (c.released && (!e.lastReleased || c.number > e.lastReleased.number)) e.lastReleased = c;
      m.set(c.team_id, e);
    }
    return m;
  }, [cycles.data]);
  const openCycles = (cycles.data ?? []).filter((c) => c.status === "open");
  const released = (cycles.data ?? []).filter((c) => c.released);

  return (
    <div className="vx-content">
      <PageHeader
        title="Pulso de Clima"
        description="Clima quinzenal por equipe de cliente, 5 afirmações. Respostas anônimas por construção; resultado só com o ciclo encerrado e pelo menos 5 respostas, imposto pelo banco."
        actions={
          canWrite ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nova equipe
            </Button>
          ) : null
        }
      />
      {installed.data === false ? (
        <div className="vx-section">
          <Callout tone="warn" title="Pulso não instalado">
            Instalar publica o formulário anônimo de 5 afirmações.
            {isAdmin ? (
              <div style={{ marginTop: 8 }}>
                <Button variant="primary" loading={install.isPending} onClick={() => install.mutate(undefined)}>
                  Instalar Pulso
                </Button>
              </div>
            ) : null}
          </Callout>
        </div>
      ) : null}
      <KpiRow>
        <KpiTile label="Ciclos em coleta" value={formatInt(openCycles.length)} foot={openCycles.length ? `${formatInt(openCycles.reduce((a, c) => a + c.response_count, 0))} de ${formatInt(openCycles.reduce((a, c) => a + c.invited_count, 0))} responderam` : "nenhum aberto"} />
        <KpiTile label="Resultados liberados" value={formatInt(released.length)} foot="ciclos encerrados com n ≥ mínimo" />
        <KpiTile label="Alertas de queda (90 dias)" value={formatInt(alerts.data?.length ?? 0)} tone={(alerts.data?.length ?? 0) > 0 ? "crit" : undefined} />
      </KpiRow>
      <Section title="Equipes" id="equipes">
        <DataTable<Team>
          caption="Equipes do Pulso"
          source={source}
          rowKey={(t) => t.id}
          onRowClick={(t) => navigate(`/diagnostico/pulso/${t.id}`)}
          initialSort={{ key: "name", dir: "asc" }}
          initialFilters={{ active: "sim" }}
          searchPlaceholder="Buscar por equipe ou cliente"
          exportName="pulso-equipes"
          empty="Nenhuma equipe. Cadastre a equipe do cliente e os membros para abrir o primeiro ciclo."
          filters={[{ key: "active", label: "Situação", all: "Todas", options: [{ value: "sim", label: "Ativas" }, { value: "nao", label: "Inativas" }] }]}
          columns={[
            {
              key: "name",
              header: "Equipe",
              sortable: true,
              cell: (t) => (
                <span className="vx-table__primary">
                  <Link to={`/diagnostico/pulso/${t.id}`} onClick={(e) => e.stopPropagation()}>
                    {t.name}
                  </Link>
                  {t.client_name ? <span className="vx-table__sub">{t.client_name}</span> : null}
                </span>
              ),
            },
            { key: "cadence_days", header: "Cadência", align: "right", cell: (t) => <span className="num">{t.cadence_days} dias</span>, csv: (t) => t.cadence_days },
            {
              key: "open",
              header: "Ciclo atual",
              cell: (t) => {
                const o = byTeam.get(t.id)?.open;
                return o ? (
                  <span className="vx-table__primary">
                    <span className="num">
                      nº {o.number} · até {formatDate(o.ends_on)}
                    </span>
                    <span className="vx-table__sub num">
                      {o.response_count} de {o.invited_count} responderam
                    </span>
                  </span>
                ) : (
                  <span className="muted">sem ciclo aberto</span>
                );
              },
              csv: (t) => byTeam.get(t.id)?.open?.number ?? "",
            },
            {
              key: "score",
              header: "Última nota",
              align: "right",
              cell: (t) => {
                const r = byTeam.get(t.id)?.lastReleased;
                return r ? (
                  <span className="vx-table__primary" style={{ alignItems: "flex-end" }}>
                    <span className="num">{fmtScore(r.score)}</span>
                    <span className={`vx-table__sub num${r.delta !== null && Number(r.delta) <= -t.drop_threshold ? " text-crit" : ""}`}>{fmtDelta(r.delta)}</span>
                  </span>
                ) : (
                  <span className="muted">-</span>
                );
              },
              csv: (t) => byTeam.get(t.id)?.lastReleased?.score ?? "",
            },
            { key: "min_responses", header: "Mínimo", kind: "number" },
            {
              key: "active",
              header: "Situação",
              cell: (t) => (!t.active ? <StatusPill plain>inativa</StatusPill> : byTeam.get(t.id)?.lastReleased?.drop_alert ? <StatusPill tone="crit">queda</StatusPill> : <StatusPill tone="ok">ativa</StatusPill>),
              csv: (t) => (t.active ? "ativa" : "inativa"),
            },
          ]}
        />
      </Section>
      <TeamDialog team={null} open={creating} onOpenChange={setCreating} />
    </div>
  );
}
